import { createPath, History, LocationDescriptorObject, LocationListener, parsePath, UnregisterCallback } from 'history'
import * as fromPathUtils from 'history/PathUtils'
import { parse, stringify } from 'query-string'
import { isObjectLike, isString } from '../../shared'

// const parser = document.createElement('a')

interface DeepLocation {
  pathname?: string
  search?: Record<string, string>
  hash?: Record<string, string>
}

export class RouterService {
  constructor(private history: History, private browserLocation: Location = document.location) {}

  get provider() {
    return this.history
  }

  /**
   * Some browsers (mainly IE 11 on win 10) does not have this property, so we need to build it manually...
   */
  get origin(): string {
    const nativeOrigin = this.browserLocation.origin

    if (nativeOrigin) {
      return nativeOrigin
    }

    const { protocol, hostname, port } = this.browserLocation
    const portValue = port ? `:${port}` : ''

    return `${protocol}//${hostname}${portValue}`
  }

  get url(): string {
    return createPath(this.history.location)
  }

  encodeURL(url: string): string {
    return encodeURIComponent(url || '')
  }
  decodeURL(url: string): string {
    return decodeURIComponent(url || '')
  }

  redirectTo(pathname: string, state?: object): void
  redirectTo(location: LocationDescriptorObject): void
  redirectTo(location: string | LocationDescriptorObject, state?: object) {
    isString(location) ? this.history.replace(location, state) : this.history.replace(location)
  }
  navigateTo(pathname: string, state?: object): void
  navigateTo(location: LocationDescriptorObject): void
  navigateTo(location: string | LocationDescriptorObject, state?: object): void {
    isString(location) ? this.history.push(location, state) : this.history.push(location)
  }
  navigateByUrl(pathname: string) {
    this.history.push(pathname)
  }
  reload(): void {
    this.browserLocation.reload()
  }

  subscribe(listener: LocationListener): UnregisterCallback {
    return this.history.listen(listener)
  }

  isActive(url: string, exact = false): boolean {
    const currentPath = this.history.location.pathname

    return exact ? currentPath === url : currentPath.startsWith(url)
  }
  /**
   * Serialize location map to string
   * @example
   * ```
   * serializeUrl({ pathname: '/one/two', search: 'orderBy="name,desc"' })
   * // serialized ==> '/one/two?orderBy=martin,name'
   *
   * serializeUrl({ pathname: '/one/two', search: {orderBy: 'name,desc'} },{encodeDeepProps:true})
   * // serialized ==> '/one/two?orderBy%3D%22name%2Cdesc%22'
   * ```
   *
   * NOTE:
   * if you provide deep location object for {hash} or {search} and turn encodeDeepProps flag on,those will be encodeURIComponent by query-string#stringify
   * @param location
   * @param deep
   */
  serializeUrl(
    location: LocationDescriptorObject | DeepLocation,
    { encodeDeepProps = false, stripLeadingSlash = false } = {}
  ): string {
    const { search, hash, pathname } = location
    const serializedSearch = isObjectLike<Record<string, string>>(search)
      ? stringify(search, { encode: encodeDeepProps })
      : search
    const serializedHash = isObjectLike<Record<string, string>>(hash)
      ? stringify(hash, { encode: encodeDeepProps })
      : hash
    const normalizedLocation = { pathname, search: serializedSearch, hash: serializedHash }
    const url = createPath(normalizedLocation)

    return stripLeadingSlash ? fromPathUtils.stripLeadingSlash(url) : url
  }

  parseUrl(url: string, deep = false) {
    const { pathname, search, hash } = parsePath(url)

    if (deep) {
      return {
        pathname,
        search: this.parseQueryParams(search),
        hash: this.parseQueryParams(hash),
      }
    }

    return { pathname, search, hash }
  }
  parseQueryParams(query: string): Record<string, string> {
    return parse(query)
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/router.service.ts